import * as THREE from 'three';
import { InputManager } from '../core/InputManager';
import { CharacterStateMachine, type CharacterState } from './CharacterStateMachine';
import { Pushable, findGrabTarget } from '../interact/Pushable';
import type { CollisionWorld } from '../world/CollisionWorld';

export interface ICharacterAnimator {
  play(state: CharacterState, speed?: number): void;
  update(deltaSeconds: number): void;
  setGroundTilt?(tiltX: number, tiltZ: number): void;
  setMotion?(vx: number, vz: number): void;
  setGrounded?(grounded: boolean): void;
  setImpact?(speed: number): void;
  setReach?(x: number, y: number, z: number, amount: number): void;
}

export const MOVEMENT = {
  walkSpeed: 3.0,
  runSpeed: 7.0,
  acceleration: 15.0,
  deceleration: 20.0,
  turnSpeedDeg: 720,
  jumpHeight: 1.2,
  gravity: -20.0,
  slopeSlideThreshold: 0.14,
  slopeSlideFriction: 2.2,
  airControl: 0.7,
  coyoteTime: 0.1,
  jumpBuffer: 0.15,
  grabRange: 1.9,
  grabMaxDist: 2.6,
  grabSlow: 0.55,
  grabRunSlow: 0.52,
  grabRunCrateMul: 1.8,
  staminaMax: 100,
  staminaDrain: 26,
  staminaRegen: 14,
  grabPullGain: 6.0,
  crateMaxSpeed: 2.2,
  /** Zona mati tarikan (m): kelebihan jarak di bawah ini BUKAN tarikan
   *  (cuma gesekan kontak saat memutari crate) -> crate tidak terseret.
   *  Menarik beneran butuh lewat zona ini + gerak radial menjauh. */
  pullDeadband: 0.35,
  /** Turunan tanah yang masih ditempel (snap) tanpa jadi airborne.
   *  Tangga arena 0.5 m -> turun tangga tetap Walk, bukan Fall/Land
   *  tiap anak tangga (sumber flicking/patah). */
  stepDownSnap: 0.6,
  /** Tinggi maksimum yang boleh dinaiki otomatis per frame (maxStepHeight).
   *  Ramp tercuram arena naik ≤0.07 m/frame → masih legal. Apa pun yang
   *  melebihi ini ditolak: karakter TIDAK bisa 'naik sendiri' ke atas
   *  balok/dinding hanya dari dorongan/tekanan arrow (requirement #2/5). */
  stepUpMax: 0.12,
  /** Skala slide lereng saat grab: menahan crate = berpijak (bisa
   *  menarik di bidang miring, bukan melorot). */
  grabSlideScale: 0.15,
} as const;

export interface CharacterControllerDeps {
  input: InputManager;
  cameraYawProvider: () => number;
  collisionWorld: CollisionWorld;
  pushables: Pushable[];
}

export class CharacterController {
  readonly root: THREE.Object3D;
  readonly stateMachine = new CharacterStateMachine();
  private animator: ICharacterAnimator;
  private deps: CharacterControllerDeps;

  private velocity = new THREE.Vector3();
  private verticalVelocity = 0;
  private grounded = true;
  private coyoteTimer = 0;
  private jumpBufferTimer = 0;
  private landingTimer = 0;
  private grabbed: Pushable | null = null;
  private grabDist = 1.2;
  /** Jeda tunggu antar Land yang di-force (anti patah ganda). */
  private landCooldown = 0;
  /** Apakah sedang tarik-lari (mempengaruhi cap kecepatan crate). */
  private runPulling = false;
  /** Energi tarik-lari 0..100. */
  private stamina = 100;

  private tmpForward = new THREE.Vector3();
  private tmpRight = new THREE.Vector3();
  private tmpMove = new THREE.Vector3();
  private tmpAim = new THREE.Vector3();
  private tmpAnchorP = new THREE.Vector3();
  private tmpAnchorN = new THREE.Vector3();

  private radius = 0.55;
  private height = 0.9;
  private groundTiltX = 0;
  private groundTiltZ = 0;
  private sendTiltX = 0;
  private sendTiltZ = 0;
  /** 0 = bebas bergerak, 1 = terjepit (nempel crate/dinding sambil menekan).
   *  Gerak aktual vs kecepatan perintah: terjepit head-on -> dorong penuh;
   *  menyerempet lewat -> nyaris nol (tidak ikut menyeret crate). */
  private blockFactor = 0;
  private lastImpact = 0;
  /** Tinggi tertinggi saat airborne (untuk splash jejak proporsional jatuh). */
  private maxAirY = 0;
  private _fallHeight = 0;

  constructor(
    root: THREE.Object3D,
    animator: ICharacterAnimator,
    deps: CharacterControllerDeps,
  ) {
    this.root = root;
    this.animator = animator;
    this.deps = deps;
  }

  get isGrounded(): boolean {
    return this.grounded;
  }

  get position(): THREE.Vector3 {
    return this.root.position;
  }

  /**
   * Arah bidik BEBAS = arah kamera (crosshair), bukan hadap badan slime.
   * Grab/marker mengikuti ke mana pemain memandang via mouse.
   */
  private aimForward(out: THREE.Vector3): THREE.Vector3 {
    const yaw = this.deps.cameraYawProvider();
    return out.set(-Math.sin(yaw), 0, -Math.cos(yaw));
  }

  getState(): CharacterState {
    return this.stateMachine.state;
  }

  isGrabbing(): boolean {
    return this.grabbed !== null;
  }

  getGrabbed(): Pushable | null {
    return this.grabbed;
  }

  getGrabDist(): number {
    return this.grabDist;
  }

  getStamina(): number {
    return this.stamina;
  }

  /** Kecepatan impact saat terakhir mendarat (m/detik, 0 bila lentur). */
  get impactSpeed(): number {
    return this.lastImpact;
  }

  /** Tinggi jatuh pada pendaratan terakhir (m). */
  get fallHeight(): number {
    return this._fallHeight;
  }

  /** Reset penuh ke spawn (untuk ganti stage): posisi, gerak, state. */
  resetTo(x: number, y: number, z: number): void {
    this.releaseGrab();
    this.root.position.set(x, y, z);
    this.root.rotation.y = 0;
    this.velocity.set(0, 0, 0);
    this.verticalVelocity = 0;
    this.grounded = true;
    this.coyoteTimer = 0;
    this.jumpBufferTimer = 0;
    this.landingTimer = 0;
    this.grabDist = 1.2;
    this.blockFactor = 0;
    this.stamina = MOVEMENT.staminaMax;
    this.landCooldown = 0;
    this.lastImpact = 0;
    this._fallHeight = 0;
    this.maxAirY = y;
    this.stateMachine.forceState('Idle');
  }

  /** Kandidat grab untuk indikator (didekati, belum di-grab). */
  getGrabCandidate(): Pushable | null {
    if (this.grabbed || !this.grounded) return null;
    const s = this.stateMachine.state;
    if (s !== 'Idle' && s !== 'Walk' && s !== 'Run') return null;
    return findGrabTarget(this.root.position, this.aimForward(this.tmpAim), this.deps.pushables, MOVEMENT.grabRange);
  }

  update(delta: number): void {
    const input = this.deps.input;

    const preX = this.root.position.x;
    const preZ = this.root.position.z;

    const moveZ = (input.isDown('forward') ? 1 : 0) - (input.isDown('back') ? 1 : 0);
    const moveX = (input.isDown('right') ? 1 : 0) - (input.isDown('left') ? 1 : 0);
    const hasMove = moveX !== 0 || moveZ !== 0;
    this.landCooldown = Math.max(0, this.landCooldown - delta);

    const running = input.isDown('run') && moveZ >= 0 && hasMove;

    if (input.wasPressed('jump')) this.jumpBufferTimer = MOVEMENT.jumpBuffer;
    else this.jumpBufferTimer = Math.max(0, this.jumpBufferTimer - delta);

    this.coyoteTimer = this.grounded
      ? MOVEMENT.coyoteTime
      : Math.max(0, this.coyoteTimer - delta);

    const interactPressed = input.consumeInteract();
    const interactReleased = input.consumeInteractRelease();
    this.updateGrab(interactPressed, interactReleased);

    if (this.grabbed) {
      const massFactor = 1.6 / (0.6 + this.grabbed.mass);
      // Tarik sambil lari: butuh stamina. Energi habis -> turun ke jalan
      // pelan sambil regen.
      this.runPulling = running && this.stamina > 0;
      const wantRunPull = this.runPulling;
      const scale = wantRunPull
        ? MOVEMENT.grabRunSlow * massFactor
        : MOVEMENT.grabSlow * massFactor;
      this.updateMovement(delta, moveX, moveZ, hasMove, wantRunPull, scale);
      this.driveGrabbed();
      if (wantRunPull && hasMove) {
        this.stamina = Math.max(0, this.stamina - MOVEMENT.staminaDrain * delta);
      } else {
        this.stamina = Math.min(100, this.stamina + MOVEMENT.staminaRegen * delta);
      }
    } else {
      this.runPulling = false;
      this.updateMovement(delta, moveX, moveZ, hasMove, running);
      // Tanpa grab tetap regen (idle di bar penuh).
      this.stamina = Math.min(100, this.stamina + MOVEMENT.staminaRegen * delta);
    }

    const jumpTriggered = this.jumpBufferTimer > 0 && (this.grounded || this.coyoteTimer > 0);
    if (jumpTriggered) {
      this.verticalVelocity = Math.sqrt(2 * -MOVEMENT.gravity * MOVEMENT.jumpHeight);
      this.grounded = false;
      this.coyoteTimer = 0;
      this.jumpBufferTimer = 0;
    }

    if (this.grounded) this.applySlopeSlide(delta, this.grabbed ? MOVEMENT.grabSlideScale : 1);

    this.applyTransform(delta, hasMove, running);
    this.resolveHorizontal();
    this.resolveCrates();
    this.updateBlockFactor(delta, preX, preZ);
    this.updateVertical(delta);

    this.stateMachine.evaluate({
      grounded: this.grounded,
      moveInput: hasMove,
      running,
      jumpTriggered,
      grabTriggered: this.grabbed !== null,
      rising: this.verticalVelocity > 0,
    });

    if (this.stateMachine.state === 'Land') {
      this.landingTimer -= delta;
      if (this.landingTimer <= 0) {
        this.stateMachine.forceState(hasMove ? (running ? 'Run' : 'Walk') : 'Idle');
      }
    }

    this.animator.play(this.stateMachine.state, this.velocity.length());
    const yaw = this.root.rotation.y;
    const cosY = Math.cos(yaw);
    const sinY = Math.sin(yaw);
    if (this.grounded) {
      this.sendTiltX = this.groundTiltX;
      this.sendTiltZ = this.groundTiltZ;
    }
    // Saat airborne, kirim tilt terakhir (latch): pelepasan bentuk tanah
    // memudar bertahap lewat smoothing animator, bukan patah satu frame.
    const localTiltX = cosY * this.sendTiltX - sinY * this.sendTiltZ;
    const localTiltZ = sinY * this.sendTiltX + cosY * this.sendTiltZ;
    const localVX = cosY * this.velocity.x - sinY * this.velocity.z;
    const localVZ = sinY * this.velocity.x + cosY * this.velocity.z;
    this.animator.setGroundTilt?.(localTiltX, localTiltZ);
    this.animator.setMotion?.(localVX, localVZ);
    this.animator.setGrounded?.(this.grounded);
    this.animator.setImpact?.(this.stateMachine.state === 'Land' ? this.lastImpact : 0);
    let reachLX = 0;
    let reachLY = 0;
    let reachLZ = 0;
    let reachAmt = 0;
    if (this.grabbed) {
      const ax = this.grabbed.pos.x - this.root.position.x;
      const az = this.grabbed.pos.z - this.root.position.z;
      const len = Math.hypot(ax, az);
      if (len > 1e-4) {
        reachLX = cosY * (ax / len) - sinY * (az / len);
        reachLZ = sinY * (ax / len) + cosY * (az / len);
        reachAmt = 0.35;
      }
    }
    this.animator.setReach?.(reachLX, reachLY, reachLZ, reachAmt);
    this.animator.update(delta);
  }

  private applySlopeSlide(delta: number, gripScale: number): void {
    const tiltMag = Math.hypot(this.groundTiltX, this.groundTiltZ);
    if (tiltMag < MOVEMENT.slopeSlideThreshold) return;

    const incline = Math.atan(tiltMag);
    const slideAccel =
      -MOVEMENT.gravity * Math.sin(incline) * MOVEMENT.slopeSlideFriction * gripScale;

    const downX = -this.groundTiltX / tiltMag;
    const downZ = -this.groundTiltZ / tiltMag;

    this.velocity.x += downX * slideAccel * delta;
    this.velocity.z += downZ * slideAccel * delta;
  }

  private updateMovement(
    delta: number,
    moveX: number,
    moveZ: number,
    hasMove: boolean,
    running: boolean,
    speedScale = 1,
  ): void {
    const yaw = this.deps.cameraYawProvider();
    this.tmpForward.set(-Math.sin(yaw), 0, -Math.cos(yaw));
    this.tmpRight.set(Math.cos(yaw), 0, -Math.sin(yaw));

    this.tmpMove.set(0, 0, 0);
    if (hasMove) {
      this.tmpMove
        .addScaledVector(this.tmpForward, moveZ)
        .addScaledVector(this.tmpRight, moveX)
        .normalize();
    }

    const targetSpeed = hasMove ? (running ? MOVEMENT.runSpeed : MOVEMENT.walkSpeed) * speedScale : 0;
    const rate = hasMove ? MOVEMENT.acceleration : MOVEMENT.deceleration;
    const desiredX = this.tmpMove.x * targetSpeed;
    const desiredZ = this.tmpMove.z * targetSpeed;

    this.velocity.x = THREE.MathUtils.damp(this.velocity.x, desiredX, rate, delta);
    this.velocity.z = THREE.MathUtils.damp(this.velocity.z, desiredZ, rate, delta);
  }

  private updateVertical(delta: number): void {
    // Lereng tempat kaki berpijak PADA FRAME SEBELUMNYA: untuk membedakan
    // kenaikan kecil di SEAM lereng->datar (sah, tidak nyangkut) vs
    // menaiki balok/dinding dari tanah datar (harus lompat/dorong).
    const prevSlope = Math.hypot(this.groundTiltX, this.groundTiltZ);
    const sample = this.deps.collisionWorld.sampleGroundHeight(
      this.root.position.x,
      this.root.position.z,
      // Filter kepala: platform yang badannya di ATAS kepala pemain bukan
      // lantai (platform melayang tidak boleh "menyedot" pemain dari bawah).
      this.root.position.y + this.height,
    );

    const wasGrounded = this.grounded;
    let groundY = sample.valid ? sample.height : 0;
    this.groundTiltX = sample.tiltX;
    this.groundTiltZ = sample.tiltZ;
    // Puncak crate = pijakan sah (jembatan crate, crate terapung). Aturan
    // stepUp yang sama berlaku: sisi crate yang tinggi tetap dinding.
    const crateTop = this.crateTopAt(this.root.position.x, this.root.position.z);
    if (crateTop !== null && crateTop > groundY) {
      groundY = crateTop;
      this.groundTiltX = 0;
      this.groundTiltZ = 0;
    }

    if (this.grounded) {
      const stepUp = groundY - this.root.position.y;
      const stepDown = this.root.position.y - groundY;
      // Kenaikan di atas lereng (atau keluar lereng ke permukaan datar)
      // sampai 0.5 m adalah CONTINIUTAS TERRAIN (puncak ramp menyambung
      // panggung/pedestal) -> boleh dinaiki berjalan. Di tanah datar
      // kenaikan > stepUpMax tetap dilarang (anti 'naik sendiri' balok).
      const onSlope = prevSlope > 0.1 || Math.hypot(sample.tiltX, sample.tiltZ) > 0.1;
      const maxStepUp = onSlope ? 0.5 : MOVEMENT.stepUpMax;
      if (stepUp > maxStepUp) {
        this.verticalVelocity = 0;
        return;
      }
      if (stepDown > MOVEMENT.stepDownSnap) {
        // Jatuh beneran: jadi airborne, state Fall, animasi lepas bertahap.
        this.grounded = false;
      } else {
        // Turun kecil (tangga/tepian rendah): tetap grounded + snap.
        this.verticalVelocity = 0;
        this.root.position.y = groundY;
        return;
      }
    }

    this.verticalVelocity += MOVEMENT.gravity * delta;
    this.root.position.y += this.verticalVelocity * delta;

    if (this.root.position.y <= groundY) {
      this.root.position.y = groundY;
      if (!wasGrounded && this.verticalVelocity < -1) {
        // Jeda anti-dobel-Land: pendaratan yang beruntun dekat (mis.
        // turun lewat tepi lalu cekung/naik lagi) tak boleh memicu
        // Land 2x dalam waktu singkat — itu yang tampak 'patah'.
        if (this.landCooldown <= 0) {
          this.stateMachine.forceState('Land');
          this.landingTimer = 0.2;
          this.lastImpact = -this.verticalVelocity;
          this._fallHeight = Math.max(0, this.maxAirY - this.root.position.y);
          this.landCooldown = 0.3;
        } else {
          this.lastImpact = 0;
          this._fallHeight = 0;
        }
      } else if (!wasGrounded) {
        this.lastImpact = 0;
        this._fallHeight = 0;
      }
      this.verticalVelocity = 0;
      this.grounded = true;
      this.maxAirY = this.root.position.y;
    } else {
      this.grounded = false;
      if (this.root.position.y > this.maxAirY) this.maxAirY = this.root.position.y;
    }
  }

  private applyTransform(delta: number, hasMove: boolean, running: boolean): void {
    this.root.position.x += this.velocity.x * delta;
    this.root.position.z += this.velocity.z * delta;

    if (!hasMove) return;

    const speedFactor = running ? 1 : 0.7;
    const targetYaw = Math.atan2(this.velocity.x, this.velocity.z);
    const currentYaw = this.root.rotation.y;
    let diff = targetYaw - currentYaw;
    diff = Math.atan2(Math.sin(diff), Math.cos(diff));
    const maxStep = THREE.MathUtils.degToRad(MOVEMENT.turnSpeedDeg) * delta * speedFactor;
    this.root.rotation.y = currentYaw + THREE.MathUtils.clamp(diff, -maxStep, maxStep);
  }

  private resolveHorizontal(): void {
    const { resolved } = this.deps.collisionWorld.resolveAABB(
      this.root.position,
      this.radius,
      this.height,
      { x: this.velocity.x, z: this.velocity.z },
    );
    this.root.position.x = resolved.x;
    this.root.position.z = resolved.z;
  }

  private updateGrab(pressed: boolean, released: boolean): void {
    if (pressed && !this.grabbed && this.grounded) {
      const s = this.stateMachine.state;
      if (s === 'Idle' || s === 'Walk' || s === 'Run') {
        const found = findGrabTarget(
          this.root.position,
          this.aimForward(this.tmpAim),
          this.deps.pushables,
          MOVEMENT.grabRange,
        );
        if (found) {
          this.grabbed = found;
          const dx = found.pos.x - this.root.position.x;
          const dz = found.pos.z - this.root.position.z;
          // Tidak lebih rapat dari jarak kontak fisik (hindari tarik vs block).
          const contact =
            this.radius + Math.max(found.halfX, found.halfZ) + 0.1;
          this.grabDist = THREE.MathUtils.clamp(
            Math.hypot(dx, dz),
            contact,
            MOVEMENT.grabRange,
          );
          // Kunci titik tempel di permukaan LUAR crate menghadap slime
          // (disimpan dlm frame lokal crate -> ikut yaw/lean, statis di
          // sisi yang sama, tidak bergeser ke sisi lain).
          this.lockGrabAnchor(found);
          this.stateMachine.forceState('Grab');
        }
      }
    }

    if (!this.grabbed) return;
    // Lepas hanya saat tombol dilepas / crate ketinggalan terlalu jauh.
    // Lompat TIDAK membatalkan grab: rubber tetap menempel ke crate.
    if (released) {
      this.releaseGrab();
      return;
    }
    const dx = this.grabbed.pos.x - this.root.position.x;
    const dz = this.grabbed.pos.z - this.root.position.z;
    if (Math.hypot(dx, dz) > MOVEMENT.grabMaxDist) this.releaseGrab();
  }

  private releaseGrab(): void {
    if (this.grabbed) {
      this.grabbed.yawTarget = null;
      this.grabbed.clearAnchor();
      this.grabbed = null;
    }
  }

  /**
   * Titik tempel: muka dominan crate menghadap slime, dengan offset
   * tangensial DIKLEMP ke dalam muka (di luar jangkauan tepi/sudut).
   * Pad pipih dijamin berada SEUTUHNYA di satu sisi crate dari arah
   * grab mana pun -> selalu sejajar rata (menempel penuh, tak miring).
   */
  private lockGrabAnchor(crate: Pushable): void {
    const sx = this.root.position.x - crate.pos.x;
    const sz = this.root.position.z - crate.pos.z;
    const sLen = Math.hypot(sx, sz);
    if (sLen > 1e-4) {
      // Margin jari-jari pad (slack strain maks ~0.24 + cadangan).
      const padR = 0.28;
      const clampT = (v: number, half: number): number =>
        Math.max(-1, Math.min(1, v / Math.max(half, 1e-4))) * Math.max(0.05, half - padR);
      const ax = Math.abs(sx) / crate.halfX;
      const az = Math.abs(sz) / crate.halfZ;
      const y = crate.pos.y + crate.height * 0.6;
      if (ax >= az) {
        // Muka normal X: tangen = Z.
        this.tmpAnchorP.set(
          crate.pos.x + Math.sign(sx) * crate.halfX,
          y,
          crate.pos.z + clampT(sz, crate.halfZ),
        );
        this.tmpAnchorN.set(Math.sign(sx), 0, 0);
      } else {
        // Muka normal Z: tangen = X.
        this.tmpAnchorP.set(
          crate.pos.x + clampT(sx, crate.halfX),
          y,
          crate.pos.z + Math.sign(sz) * crate.halfZ,
        );
        this.tmpAnchorN.set(0, 0, Math.sign(sz));
      }
    } else {
      this.tmpAnchorP.set(crate.pos.x, crate.pos.y + crate.height + 0.02, crate.pos.z);
      this.tmpAnchorN.set(0, 1, 0);
    }
    crate.lockAnchor(this.tmpAnchorP, this.tmpAnchorN);
  }

  /** Dorong saat menekan crate, tarik saat menjauh (pegas ikut massa).
   *  Hanya aksi SAH yang menggeser crate:
   *  - TARIK: jarak lewat zona mati pullDeadband + gerak radial menjauh
   *    (kekuatan ikut fraksi radial: lurus penuh, serong sebagian,
   *    tangensial nol). Memutari crate -> crate diam di tempat.
   *  - DORONG: menekan head-on sampai terjepit (blockFactor). Menyerempet
   *    lewat sisi crate tidak ikut membuldoser crate.
   *  Terpisah dari geser: BADAN crate selalu menoleh (yaw) mengikuti arah
   *  tarikan — sisi tempel menghadap penarik seperti narik kotak pakai tali. */
  private driveGrabbed(): void {
    const c = this.grabbed;
    if (!c) return;
    const faceAngle = c.anchorFaceAngle();
    if (faceAngle !== null) {
      const playerAngle = Math.atan2(
        this.root.position.x - c.pos.x,
        this.root.position.z - c.pos.z,
      );
      c.yawTarget = playerAngle - faceAngle;
    } else {
      c.yawTarget = null;
    }
    const cap =
      (this.runPulling ? MOVEMENT.crateMaxSpeed * MOVEMENT.grabRunCrateMul : MOVEMENT.crateMaxSpeed) /
      Math.sqrt(c.mass);
    const dx = c.pos.x - this.root.position.x;
    const dz = c.pos.z - this.root.position.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 1e-4) return;
    const speed = Math.hypot(this.velocity.x, this.velocity.z);
    const closing = speed > 1e-4 ? (this.velocity.x * dx + this.velocity.z * dz) / dist : 0;
    const excess = dist - this.grabDist;
    if (excess > MOVEMENT.pullDeadband && closing < 0) {
      const radialFraction = THREE.MathUtils.smoothstep(-closing / Math.max(speed, 1e-4), 0.15, 0.55);
      if (radialFraction > 0) {
        const pull = Math.min(
          (excess - MOVEMENT.pullDeadband) * (MOVEMENT.grabPullGain / c.mass) * radialFraction,
          cap,
        );
        c.vel.x = -(dx / dist) * pull;
        c.vel.z = -(dz / dist) * pull;
      }
    } else if (dist <= this.grabDist) {
      if (closing > 0.3) {
        const sp = Math.min(speed, cap) * this.blockFactor;
        c.vel.x = (dx / dist) * sp;
        c.vel.z = (dz / dist) * sp;
      }
    }
  }

  /** Perbarui blockFactor dari gerak aktual vs perintah (frame ini). */
  private updateBlockFactor(delta: number, preX: number, preZ: number): void {
    let target = 0;
    const cmdSpeed = Math.hypot(this.velocity.x, this.velocity.z);
    if (delta > 1e-6 && cmdSpeed > 0.3) {
      const actualSpeed =
        Math.hypot(this.root.position.x - preX, this.root.position.z - preZ) / delta;
      target = THREE.MathUtils.clamp(1 - actualSpeed / cmdSpeed, 0, 1);
    }
    // Nempel cepat (dorong responsif), lepas lambat (crate yang mengalah
    // tidak membuat dorongan berdenyut).
    const rate = target > this.blockFactor ? 14 : 2.5;
    this.blockFactor += (target - this.blockFactor) * (1 - Math.exp(-rate * delta));
  }

  /** Player terhalang crate: dorong keluar; crate bebas di-sodok ringan. */
  /** Puncak crate tertinggi di (x,z), atau null bila tak ada crate di bawah.
   *  Dipakai agar pemain bisa berdiri/melompat di atas crate (jembatan). */
  private crateTopAt(x: number, z: number): number | null {
    let top: number | null = null;
    for (const c of this.deps.pushables) {
      if (Math.abs(x - c.pos.x) > c.halfX || Math.abs(z - c.pos.z) > c.halfZ) continue;
      const t = c.pos.y + c.height;
      if (top === null || t > top) top = t;
    }
    return top;
  }

  private resolveCrates(): void {
    const p = this.root.position;
    for (const c of this.deps.pushables) {
      // Berdiri di atas crate (kaki di puncak): jangan didorong jatuh.
      if (p.y >= c.pos.y + c.height - 0.12) continue;
      const cx = THREE.MathUtils.clamp(p.x, c.pos.x - c.halfX, c.pos.x + c.halfX);
      const cz = THREE.MathUtils.clamp(p.z, c.pos.z - c.halfZ, c.pos.z + c.halfZ);
      let dx = p.x - cx;
      let dz = p.z - cz;
      const d2 = dx * dx + dz * dz;
      if (d2 >= this.radius * this.radius) continue;
      let nx: number;
      let nz: number;
      if (d2 > 1e-8) {
        const d = Math.sqrt(d2);
        nx = dx / d;
        nz = dz / d;
        p.x = cx + nx * this.radius;
        p.z = cz + nz * this.radius;
      } else {
        const penMinX = p.x + this.radius - (c.pos.x - c.halfX);
        const penMaxX = c.pos.x + c.halfX - (p.x - this.radius);
        const penMinZ = p.z + this.radius - (c.pos.z - c.halfZ);
        const penMaxZ = c.pos.z + c.halfZ - (p.z - this.radius);
        const penX = Math.min(penMinX, penMaxX);
        const penZ = Math.min(penMinZ, penMaxZ);
        if (penX < penZ) {
          nx = penMinX < penMaxX ? -1 : 1;
          nz = 0;
          p.x += nx * penX;
        } else {
          nx = 0;
          nz = penMinZ < penMaxZ ? -1 : 1;
          p.z += nz * penZ;
        }
      }
      const approaching = -(this.velocity.x * nx + this.velocity.z * nz);
      if (c !== this.grabbed && approaching > 0.3) {
        const cap = MOVEMENT.crateMaxSpeed / Math.sqrt(c.mass);
        const sp = Math.min(Math.hypot(this.velocity.x, this.velocity.z), cap);
        c.vel.x = -nx * sp;
        c.vel.z = -nz * sp;
      }
    }
  }
}